<template>
  <article class="self-service-card wide-card conference-payment-card">
    <div class="conf-pay-header">
      <button type="button" class="ghost-action compact" @click="front.backToConferenceList">← 返回会议列表</button>
      <h3>缴纳会议注册费</h3>
    </div>

    <ol class="conf-fee-steps" aria-label="会议费缴纳流程">
      <li v-for="step in feeSteps" :key="step.key" :class="stepItemClass(step.key)">
        <span class="conf-fee-steps__index">{{ stepIndexDisplay(step) }}</span>
        <span class="conf-fee-steps__label">{{ step.label }}</span>
      </li>
    </ol>

    <div
      v-if="front.conferenceFeedback"
      class="service-notice warning"
      role="alert"
    >
      <strong>提示</strong>
      <span>{{ front.conferenceFeedback }}</span>
    </div>
    <div
      v-else-if="paymentStatusMessage"
      class="service-notice"
      :class="paymentStatusTone"
      role="status"
    >
      <span>{{ paymentStatusMessage }}</span>
    </div>

    <div v-if="!front.isLoggedIn" class="service-notice warning">
      <strong>请先登录</strong>
      <span>登录并完成参与方式选择后即可报名学术会议。</span>
    </div>

    <div v-else-if="!registration && canConfirmLockPrice" class="conf-pay-body">
      <div class="conf-block__head">
        <strong>{{ front.selectedConference.title || front.selectedConference.conferenceTitle || '当前会议' }}</strong>
        <small>
          {{ front.conferenceFeeTypeLabel(previewFeeType) }} · {{ front.formatFee(previewFeeAmount) }}
          （确认后锁价）
        </small>
      </div>
      <div class="payment-status-panel is-locked">
        <strong>第 1 步：确认锁价报名</strong>
        <span>请确认参会档位、金额与缴费方式。选择公务卡现场支付后，还需点击下方确认按钮；确认后将直接通过并进入参会信息填写，无需上传汇款凭证和电子发票。</span>
        <div class="conference-payment-methods" role="radiogroup" aria-label="会议费缴费方式">
          <label :class="{ active: front.conferencePaymentMethod === 'BANK_TRANSFER' }">
            <input v-model="front.conferencePaymentMethod" type="radio" value="BANK_TRANSFER" :disabled="confirmingLock" />
            <span><strong>线下转账</strong><small>汇款后上传凭证与电子发票，按现有流程审核</small></span>
          </label>
          <label :class="{ active: front.conferencePaymentMethod === 'OFFICIAL_CARD_ONSITE' }">
            <input
              v-model="front.conferencePaymentMethod"
              type="radio"
              value="OFFICIAL_CARD_ONSITE"
              :disabled="confirmingLock"
            />
            <span><strong>公务卡现场支付</strong><small>现场刷公务卡；选择后请点击下方按钮确认</small></span>
          </label>
        </div>
        <div class="inline-actions">
          <button
            type="button"
            class="primary-action compact"
            :disabled="confirmingLock"
            @click="confirmLockPrice"
          >
            {{ confirmingLock ? '正在报名...' : confirmRegistrationButtonText }}
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="!registration" class="service-notice warning">
      <strong>尚未创建报名</strong>
      <span>请返回会议列表，点击「缴纳注册费」开始报名。</span>
      <button type="button" class="primary-action compact" @click="front.backToConferenceList">返回会议列表</button>
    </div>

    <section v-else class="conf-pay-body">
      <div class="conf-block__head">
        <strong>{{ front.conferenceTitleById(registration.conferenceId) }}</strong>
        <small>{{ front.conferenceFeeTypeLabel(registration.feeType) }} · {{ front.formatFee(registration.feeAmount) }}（报名时锁价）</small>
      </div>

      <div v-if="lockPriceCompleted" class="payment-status-panel is-locked">
        <strong>第 1 步：锁价报名已完成</strong>
        <span>
          已锁定档位「{{ front.conferenceFeeTypeLabel(registration.feeType) }}」，金额
          {{ front.formatFee(registration.feeAmount) }}。
          <template v-if="registration.paymentStatus === 'UNPAID' || registration.paymentStatus === 'VOUCHER_REJECTED'">
            请继续第 2 步：上传会议费凭证。
          </template>
        </span>
      </div>

      <div v-if="registration.frozenDueToExpiry === '1' && !isOfficialCardOnsite" class="payment-status-panel is-overdue">
        <strong>报名已冻结</strong>
        <span>该会议报名因会员到期冻结，请先完成会员续费后再继续。</span>
      </div>

      <template v-else>
        <div v-if="showBankInfo" class="offline-payment-card">
          <strong>线下转账说明</strong>
          <span>{{ front.offlinePaymentInfo.account }}</span>
          <span>{{ front.offlinePaymentInfo.remark }}</span>
          <span>{{ front.offlinePaymentInfo.upload }}</span>
        </div>

          <div v-if="registration.paymentStatus === 'VOUCHER_REVIEW'" class="payment-status-panel is-review">
            <strong>凭证初审中</strong>
            <span>会议费凭证已提交，请等待管理员初审。通过后可先填写参会基础信息、摘要与住宿，并请尽快上传电子发票。</span>
          </div>

        <div v-else-if="registration.paymentStatus === 'VOUCHER_REJECTED'" class="payment-status-panel is-rejected">
          <strong>凭证已驳回</strong>
          <span v-if="registration.reviewComment">驳回原因：{{ registration.reviewComment }}</span>
          <span>请核对汇款信息后重新上传凭证。</span>
        </div>

        <div v-if="canUploadVoucher" class="payment-flow__upload">
          <strong>上传会议费凭证</strong>
          <span>请先选择凭证文件，确认无误后再提交。提交后进入凭证初审，审核期间不可修改。</span>
          <label class="application-file-picker payment-file-picker">
            <span>凭证文件（JPG/PNG，≤ 5MB）</span>
            <input ref="voucherFileInput" type="file" accept=".jpg,.jpeg,.png" @change="selectVoucherFile" />
            <small v-if="pendingVoucherFile">已选择：{{ pendingVoucherFile.name }}</small>
          </label>
          <div class="inline-actions">
            <button v-if="pendingVoucherFile" type="button" class="secondary-action compact" @click="clearVoucherFile">重新选择</button>
            <button type="button" class="primary-action compact" :disabled="!pendingVoucherFile || submittingVoucher" @click="submitVoucher">
              {{ submittingVoucher ? '正在提交...' : '确认提交凭证' }}
            </button>
          </div>
        </div>

        <div v-if="registration.paymentStatus === 'INVOICE_REVIEW'" class="payment-status-panel is-review">
          <strong>发票终审中</strong>
          <span>电子发票已提交，请等待财务终审。通过后报名确认，可下载盖章通知与摘要模板。</span>
        </div>

        <div v-else-if="registration.paymentStatus === 'INVOICE_REJECTED'" class="payment-status-panel is-rejected">
          <strong>发票已驳回</strong>
          <span v-if="registration.reviewComment">驳回原因：{{ registration.reviewComment }}</span>
          <span>请重新上传电子发票。</span>
        </div>

        <div v-if="registration.paymentStatus === 'INVOICE_OVERDUE'" class="payment-status-panel is-overdue">
          <strong>发票逾期未传</strong>
          <span>已超过发票上传截止日，请尽快补传电子发票以完成报名确认。</span>
        </div>

        <div v-if="canUploadInvoice" class="payment-flow__upload">
          <strong>上传电子发票</strong>
          <span>
            凭证初审已通过，请
            <template v-if="registration.invoiceDeadline">在 {{ front.formatDate(registration.invoiceDeadline) }} 前</template>
            选择发票文件并确认提交。提交后进入发票终审，审核期间不可修改。
          </span>
          <label class="application-file-picker payment-file-picker">
            <span>发票文件（JPG/PNG/PDF，≤ 10MB）</span>
            <input ref="invoiceFileInput" type="file" accept=".jpg,.jpeg,.png,.pdf" @change="selectInvoiceFile" />
            <small v-if="pendingInvoiceFile">已选择：{{ pendingInvoiceFile.name }}</small>
          </label>
          <div class="inline-actions">
            <button v-if="pendingInvoiceFile" type="button" class="secondary-action compact" @click="clearInvoiceFile">重新选择</button>
            <button type="button" class="primary-action compact" :disabled="!pendingInvoiceFile || submittingInvoice" @click="submitInvoice">
              {{ submittingInvoice ? '正在提交...' : '确认提交发票' }}
            </button>
          </div>
        </div>

          <div v-if="front.registrationCanFillAttendee(registration)" class="conf-attendee-entry">
            <div>
              <strong>参会资料已解锁</strong>
              <small v-if="isOfficialCardOnsite">
                公务卡现场支付已登记，无需上传凭证和发票；参会信息可随时填写和修改。
              </small>
              <small v-else-if="registration.paymentStatus === 'CONFIRMED'">
                发票终审已通过，可填写报告、住宿与野外信息，并下载盖章材料。
              </small>
              <small v-else>
                凭证已通过：可先填写参会基础信息、摘要与住宿；野外与盖章材料需发票终审通过后开放。
              </small>
            </div>
            <button type="button" class="primary-action compact" @click="front.openAttendeeForm(registration)">填写参会资料 →</button>
          </div>

          <div
            v-else-if="registration.paymentStatus === 'INVOICE_OVERDUE'"
            class="service-hint"
          >
            发票已逾期，白名单填报已锁定；请先上传电子发票，终审通过后恢复全部参会能力。
          </div>

        <div v-if="registration.paymentStatus === 'CONFIRMED'" class="payment-status-panel is-confirmed">
          <strong>{{ isOfficialCardOnsite ? '公务卡现场支付已登记' : '报名已确认' }}</strong>
          <span v-if="isOfficialCardOnsite">现场支付方式已直接通过，无需线上提交汇款凭证或电子发票。</span>
          <span v-else>本场会议费已通过终审，可下载盖章通知与摘要模板。</span>
          <span v-if="registration.voucherUrl || registration.invoiceUrl" class="payment-doc-links">
            <a v-if="registration.voucherUrl" href="javascript:;" @click.prevent="front.openSecureFile(registration.voucherUrl)">查看凭证</a>
            <a v-if="registration.invoiceUrl" href="javascript:;" @click.prevent="front.openSecureFile(registration.invoiceUrl)">查看发票</a>
          </span>
        </div>

        <div v-if="registration.paymentStatus === 'CONFIRMED' && showActiveMaterials" class="conf-materials">
          <strong>会议资料下载</strong>
          <div class="file-link-list">
            <a
              v-for="file in front.selectedMaterialLinks"
              :key="file.role"
              href="javascript:;"
              @click.prevent="front.openSecureFile(file.url, file.fileName)"
            >
              <strong>{{ file.label }}</strong>
              <span>{{ file.fileName }}</span>
            </a>
          </div>
        </div>
      </template>
    </section>
  </article>
</template>

<script>
const FEE_STEPS = [
  { key: 'register', index: 1, label: '锁价报名' },
  { key: 'voucher', index: 2, label: '上传凭证' },
  { key: 'invoice', index: 3, label: '上传发票' },
  { key: 'confirmed', index: 4, label: '报名确认' }
]

export default {
  name: 'ConferencePaymentCard',
  inject: ['front'],
  data() {
    return {
      pendingVoucherFile: null,
      pendingInvoiceFile: null,
      submittingVoucher: false,
      submittingInvoice: false,
      confirmingLock: false
    }
  },
  watch: {
    'registration.registrationId'() {
      this.resetPendingFiles()
      this.syncConferenceForActiveRegistration()
    },
    'registration.paymentStatus'() {
      this.resetPendingFiles()
    }
  },
  mounted() {
    this.syncConferenceForActiveRegistration()
  },
  computed: {
    feeSteps() {
      if (this.isOfficialCardOnsite) {
        return [
          { key: 'register', index: 1, label: '选择公务卡（已完成）' },
          { key: 'confirmed', index: 2, label: '报名确认' }
        ]
      }
      return FEE_STEPS.map(step => {
        if (step.key === 'register' && this.stepProgressLevel >= 2) {
          return { ...step, label: '锁价报名（已完成）' }
        }
        return step
      })
    },
    registration() {
      return this.front.selectedRegistration
    },
    isOfficialCardOnsite() {
      return Boolean(this.registration && this.registration.paymentMethod === 'OFFICIAL_CARD_ONSITE')
    },
    lockPriceCompleted() {
      return Boolean(this.registration && this.registration.registrationId)
    },
    canConfirmLockPrice() {
      return Boolean(this.front.isLoggedIn && this.front.selectedConference)
    },
    confirmRegistrationButtonText() {
      return this.front.conferencePaymentMethod === 'OFFICIAL_CARD_ONSITE'
        ? '确认公务卡现场支付并报名'
        : '确认锁价报名'
    },
    paymentStatusMessage() {
      if (this.front.conferenceFeedback) {
        return ''
      }
      const text = String(this.front.selfServiceStatus || '').trim()
      if (!text) {
        return ''
      }
      // 仅展示与会议缴费相关的状态，避免带入会员中心其它提示
      if (/报名|会议|凭证|发票|锁价|缴费|费用|分会|参与方式|登录/.test(text)) {
        return text
      }
      return ''
    },
    paymentStatusTone() {
      const text = this.paymentStatusMessage
      if (/失败|无法|不可|请先|未|驳回|错误|截止/.test(text)) {
        return 'warning'
      }
      return 'success'
    },
    previewFeeType() {
      const identity = this.front.buildRegistrationIdentity()
      return (identity && identity.feeType) || ''
    },
    previewFeeAmount() {
      const conference = this.front.selectedConference
      if (!conference) {
        return null
      }
      return this.front.conferenceUserFeeAmount(conference)
    },
    showBankInfo() {
      if (!this.registration) {
        return false
      }
      return ['UNPAID', 'VOUCHER_REJECTED', 'VOUCHER_REVIEW'].includes(this.registration.paymentStatus)
    },
    canUploadVoucher() {
      return this.front.conferencePaymentFileRole === 'voucher'
    },
    canUploadInvoice() {
      return this.front.conferencePaymentFileRole === 'invoice'
    },
    showActiveMaterials() {
      const conference = this.front.selectedConference
      return Boolean(
        this.registration
        && conference
        && String(conference.conferenceId) === String(this.registration.conferenceId)
        && this.front.selectedMaterialLinks.length
      )
    },
    stepProgressLevel() {
      const registration = this.registration
      if (!registration) {
        return 1
      }
      const map = {
        UNPAID: 2,
        VOUCHER_REJECTED: 2,
        VOUCHER_REVIEW: 2.5,
        INVOICE_PENDING: 3,
        INVOICE_OVERDUE: 3,
        INVOICE_REJECTED: 3,
        INVOICE_REVIEW: 3.5,
        CONFIRMED: 4
      }
      return map[registration.paymentStatus] || 1
    }
  },
  methods: {
    syncConferenceForActiveRegistration() {
      const registration = this.registration
      if (!registration) {
        return
      }
      const conference = this.front.selectedConference
      if (conference && String(conference.conferenceId) === String(registration.conferenceId)) {
        return
      }
      const target = this.front.publicConferences.find(item => String(item.conferenceId) === String(registration.conferenceId))
      if (target) {
        this.front.selectConference(target)
      }
    },
    async confirmLockPrice() {
      if (this.confirmingLock) {
        return
      }
      this.front.clearConferenceFeedback()
      this.confirmingLock = true
      try {
        const created = await this.front.submitRegistration()
        if (created) {
          const registration = this.front.selectedRegistration
          if (registration && registration.paymentMethod === 'OFFICIAL_CARD_ONSITE') {
            await this.front.openAttendeeForm(registration)
          } else {
            this.front.conferenceSubView = 'payment'
          }
        }
      } catch (error) {
        this.front.setConferenceFeedback((error && error.message) || '报名失败，请稍后重试')
      } finally {
        this.confirmingLock = false
      }
    },
    resetPendingFiles() {
      this.pendingVoucherFile = null
      this.pendingInvoiceFile = null
      if (this.$refs.voucherFileInput) {
        this.$refs.voucherFileInput.value = ''
      }
      if (this.$refs.invoiceFileInput) {
        this.$refs.invoiceFileInput.value = ''
      }
    },
    selectVoucherFile(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!this.front.validateSelectedFile(file, 'payment', 'voucher')) {
        event.target.value = ''
        this.pendingVoucherFile = null
        return
      }
      this.pendingVoucherFile = file
    },
    selectInvoiceFile(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!this.front.validateSelectedFile(file, 'payment', 'invoice')) {
        event.target.value = ''
        this.pendingInvoiceFile = null
        return
      }
      this.pendingInvoiceFile = file
    },
    clearVoucherFile() {
      this.pendingVoucherFile = null
      if (this.$refs.voucherFileInput) {
        this.$refs.voucherFileInput.value = ''
      }
    },
    clearInvoiceFile() {
      this.pendingInvoiceFile = null
      if (this.$refs.invoiceFileInput) {
        this.$refs.invoiceFileInput.value = ''
      }
    },
    async submitVoucher() {
      if (!this.pendingVoucherFile) return
      this.submittingVoucher = true
      try {
        const submitted = await this.front.submitConferenceRegistrationFile('voucher', this.pendingVoucherFile)
        if (submitted) {
          this.clearVoucherFile()
        }
      } finally {
        this.submittingVoucher = false
      }
    },
    async submitInvoice() {
      if (!this.pendingInvoiceFile) return
      this.submittingInvoice = true
      try {
        const submitted = await this.front.submitConferenceRegistrationFile('invoice', this.pendingInvoiceFile)
        if (submitted) {
          this.clearInvoiceFile()
        }
      } finally {
        this.submittingInvoice = false
      }
    },
    stepIndexDisplay(step) {
      const thresholds = { register: 1, voucher: 2, invoice: 3, confirmed: 4 }
      const stepLevel = thresholds[step.key]
      if (this.stepProgressLevel >= stepLevel + 1) {
        return '✓'
      }
      return step.index
    },
    stepItemClass(stepKey) {
      const thresholds = { register: 1, voucher: 2, invoice: 3, confirmed: 4 }
      const level = this.stepProgressLevel
      const stepLevel = thresholds[stepKey]
      return {
        active: level >= stepLevel && level < stepLevel + 1,
        done: level >= stepLevel + 1
      }
    }
  }
}
</script>

<style scoped>
.conf-pay-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.conf-pay-header .ghost-action {
  align-self: flex-start;
}

.conf-pay-header h3 {
  margin: 0;
  text-align: center;
  width: 100%;
}

.conf-fee-steps {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  gap: 10px 18px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.conf-fee-steps li {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
  min-width: 64px;
  max-width: 96px;
  gap: 4px;
  text-align: center;
  color: #909399;
  font-size: 12px;
  line-height: 1.35;
}

.conf-fee-steps__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #dcdfe6;
  background: #fff;
  font-weight: 700;
  font-size: 12px;
}

.conf-fee-steps li.active {
  color: #1f6feb;
}

.conf-fee-steps li.active .conf-fee-steps__index {
  border-color: #1f6feb;
  background: #ecf5ff;
  color: #1f6feb;
}

.conf-fee-steps li.done {
  color: #67c23a;
}

.conf-fee-steps li.done .conf-fee-steps__index {
  border-color: #b7dcc5;
  background: #f0f9eb;
  color: #67c23a;
}

.conf-pay-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.conf-block__head {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.conf-block__head strong {
  font-size: 16px;
  color: #303133;
}

.conf-block__head small {
  font-size: 12px;
  color: #909399;
}

.payment-status-panel {
  padding: 12px 14px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  line-height: 1.6;
}

.payment-status-panel strong {
  font-size: 14px;
}

.payment-status-panel.is-review {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  color: #b88230;
}

.payment-status-panel.is-rejected {
  background: #fef0f0;
  border: 1px solid #fde2e2;
  color: #f56c6c;
}

.payment-status-panel.is-overdue {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #c2410c;
}

.payment-status-panel.is-locked {
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  color: #1f6feb;
}

.payment-status-panel.is-locked .inline-actions {
  margin-top: 4px;
}

.conference-payment-methods {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 4px;
}

.conference-payment-methods label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  border: 1px solid #c6d7ee;
  border-radius: 6px;
  background: #fff;
  color: #303133;
  cursor: pointer;
}

.conference-payment-methods label.active {
  border-color: #1f6feb;
  box-shadow: 0 0 0 1px #1f6feb inset;
}

.conference-payment-methods label > span,
.conference-payment-methods label strong,
.conference-payment-methods label small {
  display: block;
}

.conference-payment-methods label small {
  margin-top: 2px;
  color: #606266;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .conference-payment-methods {
    grid-template-columns: 1fr;
  }
}

.payment-status-panel.is-confirmed {
  background: #f0f9eb;
  border: 1px solid #d1edc4;
  color: #1d6b3a;
}

.payment-doc-links {
  display: flex;
  gap: 12px;
}

.payment-doc-links a {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
}

.payment-flow__upload {
  padding: 12px 14px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  color: #303133;
}

.conf-materials {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
}

.conf-materials > strong {
  font-size: 14px;
  color: #303133;
}

.conf-attendee-entry {
  padding: 12px 14px;
  border: 1px solid #d1edc4;
  border-radius: 6px;
  background: #f0f9eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.conf-attendee-entry strong {
  display: block;
  font-size: 14px;
  color: #1d6b3a;
}

.conf-attendee-entry small {
  color: #5c8a52;
  font-size: 12px;
}
</style>
